import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import Layout from './components/Layout.jsx';
import { Button } from './components/ui.jsx';
import { useAuth } from './context/AuthContext.jsx';

import Landing from './marketing/Landing.jsx';
import Login from './views/Login.jsx';
import Signup from './views/Signup.jsx';
import JoinCrew from './views/JoinCrew.jsx';
import GetTheApp from './views/GetTheApp.jsx';
import Privacy from './views/Privacy.jsx';
import ClientPortal from './views/ClientPortal.jsx';
import NotFound from './views/NotFound.jsx';

import Dashboard from './views/Dashboard.jsx';
import Dispatch from './views/Dispatch.jsx';
import LiveCoverage from './views/LiveCoverage.jsx';
import Jobs from './views/Jobs.jsx';
import JobDetail from './views/JobDetail.jsx';
import Lumpers from './views/Lumpers.jsx';
import LumperDetail from './views/LumperDetail.jsx';
import Onboarding from './views/Onboarding.jsx';
import Customers from './views/Customers.jsx';
import CustomerDetail from './views/CustomerDetail.jsx';
import Sites from './views/Sites.jsx';
import SiteDetail from './views/SiteDetail.jsx';
import Payroll from './views/Payroll.jsx';
import PayrollRun from './views/PayrollRun.jsx';
import Invoices from './views/Invoices.jsx';
import InvoiceDetail from './views/InvoiceDetail.jsx';
import Notifications from './views/Notifications.jsx';
import Leads from './views/Leads.jsx';
import Reports from './views/Reports.jsx';
import AuditLog from './views/AuditLog.jsx';
import Settings from './views/Settings.jsx';
import BrandMark from './components/BrandMark.jsx';

function RequireAuth({ children }) {
  const { actor, loading, offline } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', gap: 14 }}>
        <div className="col gap-12" style={{ alignItems: 'center' }}>
          <BrandMark size={46} />
          <span className="row gap-8 dim">
            <Loader2 size={14} className="spin" style={{ animation: 'spin 900ms linear infinite' }} />
            Loading the control panel…
          </span>
        </div>
      </div>
    );
  }
  if (!actor) {
    // Only bounce to sign-in when the API actually refused us. If dispatch is
    // unreachable (deploy restart, flaky network) keep the console open.
    if (offline) {
      return (
        <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 24 }}>
          <div className="col gap-12 center" style={{ alignItems: 'center', maxWidth: 420 }}>
            <BrandMark size={44} />
            <h3>Cannot reach dispatch</h3>
            <p className="muted">
              The API did not answer. This usually clears in a few seconds during a deploy.
            </p>
            <Button variant="primary" onClick={() => window.location.reload()}>
              Try again
            </Button>
          </div>
        </div>
      );
    }
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  // Customers get their own dashboard, not the work console.
  if (actor.role === 'client') return <Navigate to="/portal" replace />;
  return children;
}

/** Customers only. */
function RequireClient({ children }) {
  const { actor, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <span className="row gap-8 dim">
          <Loader2 size={14} style={{ animation: 'spin 900ms linear infinite' }} />
          Loading your dashboard…
        </span>
      </div>
    );
  }
  if (!actor) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (actor.role !== 'client') return <Navigate to="/app" replace />;
  return children;
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/join/:code" element={<JoinCrew />} />
      <Route path="/get-the-app" element={<GetTheApp />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route
        path="/portal"
        element={
          <RequireClient>
            <ClientPortal />
          </RequireClient>
        }
      />
      <Route
        path="/app"
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="dispatch" element={<Dispatch />} />
        <Route path="live" element={<LiveCoverage />} />
        <Route path="jobs" element={<Jobs />} />
        <Route path="jobs/:id" element={<JobDetail />} />
        <Route path="lumpers" element={<Lumpers />} />
        <Route path="lumpers/:id" element={<LumperDetail />} />
        <Route path="onboarding" element={<Onboarding />} />
        <Route path="customers" element={<Customers />} />
        <Route path="customers/:id" element={<CustomerDetail />} />
        <Route path="sites" element={<Sites />} />
        <Route path="sites/:id" element={<SiteDetail />} />
        <Route path="payroll" element={<Payroll />} />
        <Route path="payroll/:id" element={<PayrollRun />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="invoices/:id" element={<InvoiceDetail />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="leads" element={<Leads />} />
        <Route path="reports" element={<Reports />} />
        <Route path="audit" element={<AuditLog />} />
        <Route path="settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
